import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import {
  ClipboardPaste,
  Copy,
  Download,
  EllipsisVertical,
  Import,
} from 'lucide-react';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'src/componentes/dropdown';

import Alert from 'src/componentes/message-box/alert';
import Confirm from 'src/componentes/message-box/confirm';
import ContentManager from 'src/componentes/content-manager';
import http from 'src/services/httpService';
import { DownloadFile, ParseBlobError } from 'src/services/downloadFileUrl';
import errorHandler from 'src/utils/error-handler';
import css from 'src/utils/css';
import FormularioGlobalImportacao from './FormularioGlobalImportacao';
import {
  copiarFormularioParaAreaTransferencia,
  isFormularioVazio,
  lerFormularioDaAreaTransferencia,
} from './formularioGlobalClipboard';

const classes = css`
  .toggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #73737f;
    display: inline-flex;
    padding: 4px;
  }
`;

const FormularioGlobalCopiarColar = ({ disabled, formulario, onColar }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [importacaoAberta, setImportacaoAberta] = useState(false);
  const [exportando, setExportando] = useState(false);
  const exportandoRef = useRef(false);
  const currentRef = useRef({ disabled, formulario, onColar });
  currentRef.current = { disabled, formulario, onColar };

  const showAlert = (id) =>
    ContentManager.addContent(<Alert message={intl.formatMessage({ id })} />);

  const handleExportar = async () => {
    if (exportandoRef.current) return;
    exportandoRef.current = true;
    setExportando(true);
    try {
      const response = await http.post(
        '/FormularioGlobal/Exportar',
        currentRef.current.formulario,
        { responseType: 'blob' }
      );
      DownloadFile(response);
    } catch (err) {
      errorHandler(await ParseBlobError(err));
    } finally {
      exportandoRef.current = false;
      setExportando(false);
    }
  };

  const handleCopiar = async () => {
    try {
      if (isFormularioVazio(formulario)) {
        showAlert('labelFormularioVazioParaCopiar');
        return;
      }

      const copiado = await copiarFormularioParaAreaTransferencia(formulario);

      if (copiado) {
        enqueueSnackbar(intl.formatMessage({ id: 'labelFormularioCopiado' }), {
          variant: 'success',
        });
      } else {
        showAlert('labelNaoFoiPossivelCopiarFormulario');
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const aplicarColagem = (
    formularioColado,
    messageId = 'labelFormularioColado'
  ) => {
    if (currentRef.current.disabled) return;
    currentRef.current.onColar?.(formularioColado);
    enqueueSnackbar(intl.formatMessage({ id: messageId }), {
      variant: 'success',
    });
  };

  const substituirFormulario = (novoFormulario, messageId) => {
    if (currentRef.current.disabled) return;
    if (isFormularioVazio(currentRef.current.formulario)) {
      aplicarColagem(novoFormulario, messageId);
      return;
    }
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'labelSubstituirFormularioConfirmacao',
        })}
        handleConfirm={() => aplicarColagem(novoFormulario, messageId)}
      />
    );
  };

  const handleColar = async () => {
    if (currentRef.current.disabled) return;
    try {
      const formularioColado = await lerFormularioDaAreaTransferencia();

      if (!formularioColado) {
        showAlert('labelConteudoAreaTransferenciaNaoEhFormulario');
        return;
      }

      substituirFormulario(formularioColado);
    } catch (err) {
      errorHandler(err);
    }
  };

  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle asChild>
          <button
            className={classes.toggle}
            type="button"
            aria-label={intl.formatMessage({ id: 'labelAcoesFormulario' })}
            title={intl.formatMessage({ id: 'labelAcoesFormulario' })}
          >
            <EllipsisVertical size={22} />
          </button>
        </DropdownToggle>
        <DropdownMenu end>
          <DropdownItem onClick={handleCopiar}>
            <Copy strokeWidth={1.75} />
            {intl.formatMessage({ id: 'labelCopiarFormulario' })}
          </DropdownItem>
          {!disabled && (
            <DropdownItem onClick={handleColar}>
              <ClipboardPaste strokeWidth={1.75} />
              {intl.formatMessage({ id: 'labelColarFormulario' })}
            </DropdownItem>
          )}
          <DropdownItem
            disabled={disabled}
            onClick={() => setImportacaoAberta(true)}
          >
            <Import strokeWidth={1.75} />
            {intl.formatMessage({ id: 'labelImportarFormulario' })}
          </DropdownItem>
          <DropdownItem disabled={exportando} onClick={handleExportar}>
            <Download strokeWidth={1.75} />
            {intl.formatMessage({ id: 'labelExportarFormularioAtual' })}
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
      {importacaoAberta && (
        <FormularioGlobalImportacao
          disabled={disabled}
          formulario={formulario}
          onClose={() => setImportacaoAberta(false)}
          onImportar={(importado) =>
            substituirFormulario(importado, 'msgFormularioImportado')
          }
        />
      )}
    </>
  );
};

export default FormularioGlobalCopiarColar;
